// The verse's vocabulary, as data — the hover copy for every platform concept.
//
// This is a hand-authored MIRROR of the one-sentence `plain` line of each term in
// the canonical server glossary (`products/diwa/src/diwa/manual.py` MANUAL). The web
// app is a declared derivation target of that manual, so this mirror is canon, not a
// fork. Hovers must be instant and work offline, so we render from memory here rather
// than fanning an auth'd HTTP request per glyph.
//
// Drift is bounded to one line per term and GUARDED by `vocabulary.test.ts`, which
// asserts `keys(VOCAB) ⊆ MANUAL keys` and `VOCAB[k].gloss === MANUAL[k].plain` against
// the checked-in `manual.plain.json` snapshot. Adding a term = add a DATA ROW below;
// no route, validator, or component change.

export interface VocabTerm {
  /** == MANUAL[key].plain — the one-sentence hover copy. */
  gloss: string
  /** == MANUAL[key].surface — the route the optional "go there" deepen links to. */
  surface?: string
}

export const VOCAB: Record<string, VocabTerm> = {
  verse: {
    gloss: 'Your whole world in Diwa — every domain, star, and mission you hold.',
    surface: '/map',
  },
  domain: {
    gloss: 'One subject area of your verse — with its own vocabulary, springs, and rules.',
    surface: '/domains',
  },
  spring: {
    gloss: 'A pipeline that feeds a domain — a source flowing into your verse on a schedule.',
    surface: '/pipelines',
  },
  lake: {
    gloss: 'The shared pool of cleaned, queryable records that springs fill.',
    surface: '/ledger',
  },
  ledger: {
    gloss: 'The browsing surface for the lake — list, kanban, calendar, and map lenses.',
    surface: '/ledger',
  },
  signal: {
    gloss: 'A lake arrival that crossed YOUR rules and now asks for judgment.',
    surface: '/signals',
  },
  star: {
    gloss: 'Something you hold — an entity tracked in your constellation, with history.',
    surface: '/graph',
  },
  constellation: {
    gloss: 'Your stars and how they relate — the world model, drawn as a night sky.',
    surface: '/graph',
  },
  mission: {
    gloss: 'Work with steps, gates, and an outcome — the unit of getting something done.',
    surface: '/missions',
  },
  gate: {
    gloss: 'A step where work stops until YOU judge it — approve, reject, or redirect.',
    surface: '/missions',
  },
  artifact: {
    gloss: 'The written product of a mission step — a draft with citations, waiting at a gate.',
    surface: '/missions',
  },
  matcher: {
    gloss: 'The engine that reads your rules and promotes matching lake arrivals into signals.',
    surface: '/pipelines',
  },
  rules: {
    gloss: 'Your judgment, written down — what deserves to become a signal in each domain.',
    surface: '/pipelines',
  },
  trove: {
    gloss: 'Your documents and captures — files that become stars and edges.',
    surface: '/trove',
  },
  briefing: {
    gloss: "The daily read of your verse — what changed, what asks, what's warm.",
    surface: '/briefing',
  },
  tala: {
    gloss: "Diwa's familiar, named for the goddess of stars — she works your verse behind your gates.",
    surface: '/familiar',
  },
  principal: {
    gloss: 'A named key for an outside agent — scoped hands into your world.',
    surface: '/agents',
  },
  plan: {
    gloss: "What your tier includes — Tala's usage, which springs you can use, missions, and seats.",
    surface: '/pricing',
  },
  protocol: {
    gloss: "A mission's script — the named steps and gates a kind of work always follows.",
    surface: '/missions',
  },
}

/**
 * The hover copy for a registry term, or `undefined` when unknown.
 * The single lookup rule shared by HelpHint and any deepen affordance.
 */
export function glossFor(term: string | undefined | null): string | undefined {
  if (!term) return undefined
  return VOCAB[term]?.gloss
}

// ── Glyph legends, as data ────────────────────────────────────────────────
// One source for every glyph key in the app: inline HelpHints (mid-sentence
// glyphs) and the <Legend> component (glyph sets) both read from GLYPHS, so the
// copy lives once. `term` links a glyph to a MANUAL concept for an optional deepen.

export interface GlyphDef {
  glyph: string
  label: string
  term?: string
}

export const GLYPHS: Record<string, GlyphDef[]> = {
  spring: [
    { glyph: '●', label: 'healthy · last run clean' },
    { glyph: '◐', label: 'ran with warnings' },
    { glyph: '◔', label: 'degraded' },
    { glyph: '✕', label: 'failed last run' },
    { glyph: '○', label: 'never run' },
  ],
  mission: [
    { glyph: '◈', label: 'awaiting your gate', term: 'gate' },
    { glyph: '◇', label: 'review step' },
    { glyph: '◉', label: 'running' },
    { glyph: '●', label: 'complete' },
    { glyph: '○', label: 'not started' },
    { glyph: '✕', label: 'failed' },
    { glyph: '◆', label: 'won' },
    { glyph: '◐', label: 'in progress' },
  ],
  familiar: [
    { glyph: '✦', label: 'a tool call' },
    { glyph: '◆', label: 'a write' },
    { glyph: '◈', label: 'a proposal / ask', term: 'gate' },
  ],
}

/** The status phrase for a glyph within a family, or `undefined` if not in the set. */
export function glyphLabel(family: string, glyph: string): string | undefined {
  return GLYPHS[family]?.find((g) => g.glyph === glyph)?.label
}
